import { LegalEntityCountry } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { ComponentProps, useCallback, useEffect } from 'react';
import { InvoiceEvidenceView } from '../../../../types/entity-types';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { getCountryCurrency } from '../../../../utils/get-country-currency';
import { PaymentDetails } from '../../payment-flow-storage';
import { usePaymentFlow } from '../../use-payment-flow';
import { isSupportedCurrency } from '../../utils/is-supported-currency';
import { isSupportedBeneficiaryCountry } from '../utils/is-supported-beneficiary-country';
import { KnownBeneficiaryFlow } from './known-beneficiary-flow';
import { KnownBeneficiaryNotPaidBeforeFlow } from './known-beneficiary-not-paid-before-flow';
import { LoadingInvoiceWrapper } from './loading/loading-invoice-wrapper';
import { UnknownBeneficiaryFlow } from './unknown-beneficiary-flow';
import { UnsupportedInvoiceDetails } from './unsupported-invoice-details';

// TODO: improve the first two flow names, they are not detailed enough
export type BillPayFlow = 'KNOWN_BENEFICIARY' | 'UNKNOWN_BENEFICIARY' | 'KNOWN_DESTINATION_BENEFICIARY_DOES_NOT_EXIST';

type BillPayFlowsWrapperProps = {
  billPayFlow: BillPayFlow;
  evidence?: InvoiceEvidenceView;
  accounts: ComponentProps<typeof KnownBeneficiaryFlow>['accounts'];
  beneficiaries: ComponentProps<typeof KnownBeneficiaryFlow>['beneficiaries'];
  companyCountry: LegalEntityCountry;
  isLoading: ComponentProps<typeof KnownBeneficiaryFlow>['isLoading'];
  isAIEnrichingEvidence: boolean;
  onCreateBeneficiary: ComponentProps<typeof UnknownBeneficiaryFlow>['onCreateBeneficiary'];
  onMakePayment: (paymentDetails: PaymentDetails) => Promise<void>;
  onCancelTransfer: ComponentProps<typeof KnownBeneficiaryFlow>['onCancelTransfer'];
};

export const BillPayFlowsWrapper = ({
  billPayFlow,
  evidence,
  accounts,
  beneficiaries,
  companyCountry,
  isLoading,
  isAIEnrichingEvidence,
  onCreateBeneficiary,
  onMakePayment,
  onCancelTransfer,
}: BillPayFlowsWrapperProps) => {
  const { updateBillPayFlow } = usePaymentFlow();

  const cachedUpdateBillPayFlow = useCallback(updateBillPayFlow, [updateBillPayFlow]);

  useEffect(() => {
    if (billPayFlow) {
      cachedUpdateBillPayFlow(billPayFlow);
    }
  }, [billPayFlow, cachedUpdateBillPayFlow]);

  if (isAIEnrichingEvidence) {
    return <LoadingInvoiceWrapper onCancel={onCancelTransfer} />;
  }

  if (!evidence) {
    // This should never happen
    return;
  }

  const rawCurrency = evidence.invoiceDetails?.currency;
  const invoiceCurrency = rawCurrency && isSupportedCurrency(rawCurrency) ? rawCurrency : undefined;

  switch (billPayFlow) {
    case 'KNOWN_BENEFICIARY': {
      const suggestedBeneficiary = beneficiaries.find(
        (beneficiary) => beneficiary.id === evidence.suggestedBeneficiaryId,
      );

      if (!suggestedBeneficiary) {
        Sentry.captureMessage(
          `The suggested beneficiary id can't be found among the beneficiaries in KNOWN_BENEFICIARY bill pay flow`,
          {
            extra: {
              suggestedBeneficiaryId: evidence.suggestedBeneficiaryId,
              beneficiaries: beneficiaries.map((b) => b.id),
              billPayFlow,
              message: `The suggested beneficiary id can't be found among the beneficiaries in KNOWN_BENEFICIARY bill pay flow`,
            },
          },
        );

        return (
          <UnsupportedInvoiceDetails
            title="Oups, something went wrong"
            variantConfig={{ variant: 'GENERIC_ERROR' }}
            onGoBack={onCancelTransfer}
          />
        );
      }

      const suggestedBeneficiaryCurrency = suggestedBeneficiary?.destination.currency;

      const invoiceDetails = {
        totalAmount: evidence.invoiceDetails?.totalAmount,
        suggestedBeneficiaryId: evidence.suggestedBeneficiaryId,
        invoiceReference: evidence.invoiceDetails?.invoiceReference,
        currency: suggestedBeneficiaryCurrency,
      };

      return (
        <KnownBeneficiaryFlow
          accounts={accounts}
          beneficiaries={beneficiaries}
          initialInvoiceDetails={invoiceDetails}
          isLoading={isLoading}
          onMakePayment={onMakePayment}
          onCancelTransfer={onCancelTransfer}
        />
      );
    }
    case 'UNKNOWN_BENEFICIARY': {
      const invoiceDetails = {
        totalAmount: evidence.invoiceDetails?.totalAmount,
        suggestedBeneficiaryId: evidence.suggestedBeneficiaryId,
        invoiceReference: evidence.invoiceDetails?.invoiceReference,
        currency: invoiceCurrency,
      };

      return (
        <UnknownBeneficiaryFlow
          beneficiaries={beneficiaries}
          companyCountry={companyCountry}
          accounts={accounts}
          invoiceDetails={invoiceDetails}
          isLoading={isLoading}
          onGoBack={onCancelTransfer}
          onCreateBeneficiary={onCreateBeneficiary}
          onMakePayment={onMakePayment}
        />
      );
    }
    case 'KNOWN_DESTINATION_BENEFICIARY_DOES_NOT_EXIST': {
      const invoiceDetails = {
        totalAmount: evidence.invoiceDetails?.totalAmount,
        suggestedBeneficiaryId: evidence.suggestedBeneficiaryId,
        invoiceReference: evidence.invoiceDetails?.invoiceReference,
        currency: invoiceCurrency,
      };

      const vendorCountry = evidence.vendorInformation?.vendorCountry;

      if (!isSupportedBeneficiaryCountry(vendorCountry)) {
        return (
          <UnsupportedInvoiceDetails
            title="Vendor country not supported"
            variantConfig={{ variant: 'VENDOR_COUNTRY_NOT_SUPPORTED', unsupportedVendorCountry: vendorCountry }}
            onGoBack={onCancelTransfer}
          />
        );
      }

      const beneficiaryDetails: ComponentProps<typeof KnownBeneficiaryNotPaidBeforeFlow>['beneficiaryDetails'] = {
        // Fallback to company country currency when invoice currency is unavailable to pre-populate the beneficiary creation form
        currency: invoiceCurrency ?? getCountryCurrency(companyCountry),
        country: vendorCountry,
        email: evidence.vendorInformation?.vendorEmail,
        legalName: evidence.vendorInformation?.vendorCompanyName,
        iban: evidence.paymentInformation?.iban,
        accountNumber: evidence.paymentInformation?.accountNo,
        sortCode: evidence.paymentInformation?.sortCode,
      };

      return (
        <KnownBeneficiaryNotPaidBeforeFlow
          companyCountry={companyCountry}
          accounts={accounts}
          beneficiaries={beneficiaries}
          invoiceDetails={invoiceDetails}
          beneficiaryDetails={beneficiaryDetails}
          isLoading={isLoading}
          onCreateBeneficiary={onCreateBeneficiary}
          onMakePayment={onMakePayment}
          onGoBack={onCancelTransfer}
        />
      );
    }
    default:
      return exhaustiveCheck(billPayFlow);
  }
};
